import { useApolloClient, useMutation } from '@apollo/client/react';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { v5 as uuidv5 } from 'uuid';
import { useAppSelector } from '../../hooks/redux';
import { Kyc } from '../../models/profile/Kyc';
import { SAVE_KYC } from '../../queries/profile';
import { GET_KYC_PHOTO_URL } from '../../queries/utils';
import { extractStoragePath } from '../../utils/helper';

interface ModalKycProps {
    isOpen: boolean;
    onDismiss: (success?: boolean) => void;
    initialData?: Kyc;
}

const ModalKyc: React.FC<ModalKycProps> = ({ isOpen, onDismiss, initialData }) => {
    const { userId } = useAppSelector(state => state.auth);
    const profile = useAppSelector((state) => state.profile.profile);
    const apolloClient = useApolloClient();

    const [ktpNumber, setKtpNumber] = useState<string>(initialData?.ktpNumber || '');
    const [ktpCardFile, setKtpCardFile] = useState<File | null>(null);
    const [ktpSelfieFile, setKtpSelfieFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<string>('');

    const ktpCardInputRef = useRef<HTMLInputElement>(null);
    const ktpSelfieInputRef = useRef<HTMLInputElement>(null);

    const [saveKyc, { loading: saveLoading }] = useMutation(SAVE_KYC);

    useEffect(() => {
        setKtpNumber(initialData?.ktpNumber || '')
    }, [initialData])

    const handleKtpCardFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setError('KTP card photo must be an image file');
                return;
            }
            setKtpCardFile(file);
            setError(null);
        }
    };

    const handleKtpSelfieFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setError('Selfie photo must be an image file');
                return;
            }
            setKtpSelfieFile(file);
            setError(null);
        }
    };

    const generateKycPhotoPath = (type: 'ktp-card' | 'ktp-selfie'): string => {
        const personId = profile?.person.id;
        const now = new Date();
        const dateIso = now.toISOString();
        const NAMESPACE = '6ba7b810-9dad-11d1-80b4-00c04fd430c8';
        const uuid = uuidv5(dateIso, NAMESPACE);

        return `person/${personId}/kyc/${type}/${uuid}.jpg`;
    };

    const getSignedUrl = async (filename: string) => {
        const { data } = await apolloClient.query({
            query: GET_KYC_PHOTO_URL,
            variables: { sub_url: filename },
            fetchPolicy: 'no-cache'
        });
        return (data as any).url;
    };

    const uploadToFirebase = async (signedUrl: string, file: File) => {
        const response = await fetch(signedUrl, {
            method: 'PUT',
            body: file,
            headers: {
                'Content-Type': 'application/octet-stream',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to upload ${file.name}`);
        }
    };

    const extractFileName = (uri: string) => {
        const match = uri.match(/kyc\/[^/]+\/([^?]+)/);
        return match ? match[1] : uri;
    };

    const handleSave = async () => {
        setError(null);

        if (!ktpNumber) {
            setError('KTP number is required');
            return;
        }

        if (!ktpCardFile && !initialData?.ktpPhotoUri) {
            setError('KTP card photo is required');
            return;
        }

        if (!ktpSelfieFile && !initialData?.ktpSelfieUri) {
            setError('Selfie photo is required');
            return;
        }

        try {
            let ktpPhotoUri = initialData?.ktpPhotoUri;
            let ktpSelfieUri = initialData?.ktpSelfieUri;

            if (ktpCardFile) {
                setUploadProgress('Uploading KTP card photo...');
                const ktpCardPath = generateKycPhotoPath('ktp-card');
                const ktpCardSignedUrl = await getSignedUrl(ktpCardPath);
                await uploadToFirebase(ktpCardSignedUrl, ktpCardFile);
                ktpPhotoUri = `gs://${extractStoragePath(ktpCardSignedUrl)}`;
            }

            if (ktpSelfieFile) {
                setUploadProgress('Uploading selfie photo...');
                const ktpSelfiePath = generateKycPhotoPath('ktp-selfie');
                const ktpSelfieSignedUrl = await getSignedUrl(ktpSelfiePath);
                await uploadToFirebase(ktpSelfieSignedUrl, ktpSelfieFile);
                ktpSelfieUri = `gs://${extractStoragePath(ktpSelfieSignedUrl)}`;
            }

            setUploadProgress('Saving KYC data...');


            const kycData = {
                status: 'inreview',
                name: profile?.person?.getFullName(),
                ktpNumber: ktpNumber,
                ktpPhotoUri: ktpPhotoUri,
                ktpSelfieUri: ktpSelfieUri,
            };

            // Stringify kycData if required by backend or for debugging
            const kycDataString = JSON.stringify(kycData);

            await saveKyc({
                variables: {
                    kyc: kycDataString
                }
            });

            setUploadProgress('');
            onDismiss(true);
        } catch (error: any) {
            console.error('Failed to save KYC:', error);
            setError(error.message || 'Failed to save KYC data');
            setUploadProgress('');
        }
    };

    const handleClose = () => {
        onDismiss(false);
    };

    const hasExistingKtpCard = initialData?.ktpPhotoUri && !ktpCardFile;
    const hasExistingKtpSelfie = initialData?.ktpSelfieUri && !ktpSelfieFile;

    return (
        <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">KYC Verification - Indonesian Identity Card</Typography>
                    <IconButton edge="end" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {error && (
                        <Alert severity="error" onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {uploadProgress && (
                        <Alert severity="info">
                            {uploadProgress}
                        </Alert>
                    )}

                    <Box>
                        <TextField
                            label="KTP Number"
                            value={ktpNumber}
                            onChange={(e) => setKtpNumber(e.target.value)}
                            fullWidth
                            required
                            placeholder="Enter your KTP number"
                            inputProps={{ maxLength: 16 }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            KTP Card Photo *
                        </Typography>
                        <input
                            ref={ktpCardInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleKtpCardFileChange}
                            style={{ display: 'none' }}
                        />
                        <Paper
                            sx={{
                                p: 2,
                                bgcolor: 'background.default',
                                border: '2px dashed',
                                borderColor: (ktpCardFile || hasExistingKtpCard) ? 'success.main' : 'divider',
                                cursor: 'pointer',
                            }}
                            onClick={() => ktpCardInputRef.current?.click()}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {ktpCardFile ? (
                                    <>
                                        <ImageIcon color="success" />
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                                {ktpCardFile.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {(ktpCardFile.size / 1024).toFixed(2)} KB
                                            </Typography>
                                        </Box>
                                    </>
                                ) : hasExistingKtpCard ? (
                                    <>
                                        <ImageIcon color="success" />
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                                {extractFileName(initialData?.ktpPhotoUri || '')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Click to change
                                            </Typography>
                                        </Box>
                                    </>
                                ) : (
                                    <>
                                        <CloudUploadIcon color="action" />
                                        <Typography variant="body2" color="text.secondary">
                                            Click to upload KTP card photo
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </Paper>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Selfie Photo *
                        </Typography>
                        <input
                            ref={ktpSelfieInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleKtpSelfieFileChange}
                            style={{ display: 'none' }}
                        />
                        <Paper
                            sx={{
                                p: 2,
                                bgcolor: 'background.default',
                                border: '2px dashed',
                                borderColor: (ktpSelfieFile || hasExistingKtpSelfie) ? 'success.main' : 'divider',
                                cursor: 'pointer',
                            }}
                            onClick={() => ktpSelfieInputRef.current?.click()}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {ktpSelfieFile ? (
                                    <>
                                        <ImageIcon color="success" />
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                                {ktpSelfieFile.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {(ktpSelfieFile.size / 1024).toFixed(2)} KB
                                            </Typography>
                                        </Box>
                                    </>
                                ) : hasExistingKtpSelfie ? (
                                    <>
                                        <ImageIcon color="success" />
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                                {extractFileName(initialData?.ktpSelfieUri || '')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Click to change
                                            </Typography>
                                        </Box>
                                    </>
                                ) : (
                                    <>
                                        <CloudUploadIcon color="action" />
                                        <Typography variant="body2" color="text.secondary">
                                            Click to upload selfie photo
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </Paper>
                    </Box>

                    {initialData?.note && (
                        <Alert severity="warning">
                            <Typography variant="body2" fontWeight="bold">Note:</Typography>
                            <Typography variant="body2">{initialData.note}</Typography>
                        </Alert>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button variant="outlined" onClick={handleClose} disabled={saveLoading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={
                        saveLoading ||
                        !ktpNumber ||
                        (!ktpCardFile && !initialData?.ktpPhotoUri) ||
                        (!ktpSelfieFile && !initialData?.ktpSelfieUri)
                    }
                    startIcon={saveLoading ? <CircularProgress size={20} /> : undefined}
                >
                    {saveLoading ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModalKyc;
